import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Repeat, Cpu, CalendarIcon } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import MetaCard from "@/components/MetaCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { formatDate, formatDateOnly } from "@/lib/format";

const FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly (3 months)" },
  { value: "six_monthly", label: "6-Monthly" },
  { value: "annual", label: "Annual (12 months)" },
  { value: "custom_days", label: "Custom (every N days)" },
];

const EMPTY = {
  machine_id: "",
  frequency: "annual",
  custom_days: "",
  anchor_due_date: "",
  active: true,
  notes: "",
};

export default function ServiceScheduleForm() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({});
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Prefill machine_id from URL — apply AFTER machines list has loaded,
  // otherwise Radix Select silently drops the value.
  const prefillMachineIdRef = useRef(search.get("machine_id"));
  const prefillAppliedRef = useRef(false);

  // Phase P6 — Pre-fill scheduling defaults on new-schedule mount only.
  // Fetch once; do NOT overwrite fields the user has already touched.
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (isEdit || defaultsAppliedRef.current) return;
    defaultsAppliedRef.current = true;
    api.get("/settings/scheduling-defaults")
      .then(({ data }) => {
        setForm((f) => {
          const next = { ...f };
          if (f.frequency === EMPTY.frequency) next.frequency = data.default_frequency;
          if (!f.custom_days && data.default_custom_days) next.custom_days = String(data.default_custom_days);
          if (!f.anchor_due_date) {
            const d = new Date();
            d.setDate(d.getDate() + (Number(data.anchor_offset_days) || 0));
            next.anchor_due_date = d.toISOString().slice(0, 10);
          }
          return next;
        });
      })
      .catch(() => { /* defaults are convenience only — silent fail */ });
  }, [isEdit]);

  // Fetch active machines list (for new-mode picker)
  useEffect(() => {
    if (isEdit) return;
    api.get("/machines", { params: { active: true, limit: 500 } })
      .then(({ data }) => setMachines(data.items || []))
      .catch((err) => toast.error(formatApiError(err)));
  }, [isEdit]);

  // Apply URL prefill AFTER machines list is in state — Radix Select value only syncs
  // once matching SelectItem children are already mounted.
  useEffect(() => {
    if (isEdit) return;
    const mid = prefillMachineIdRef.current;
    if (mid && !prefillAppliedRef.current && machines.some((m) => m.id === mid)) {
      prefillAppliedRef.current = true;
      setForm((f) => (f.machine_id ? f : { ...f, machine_id: mid }));
    }
  }, [machines, isEdit]);

  // Load schedule in edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/service-schedules/${id}`);
        setForm({
          machine_id: data.machine_id,
          frequency: data.frequency,
          custom_days: data.custom_days || "",
          anchor_due_date: data.anchor_due_date || "",
          active: !!data.active,
          notes: data.notes || "",
        });
        setMeta({ created_at: data.created_at, updated_at: data.updated_at });
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/scheduling/schedules", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [id, isEdit, navigate]);

  // Fetch selected machine detail (for context card)
  const loadMachineDetail = useCallback(async (mid) => {
    if (!mid) { setSelectedMachine(null); return; }
    try {
      const { data } = await api.get(`/machines/${mid}`);
      setSelectedMachine(data);
    } catch { setSelectedMachine(null); }
  }, []);

  useEffect(() => { loadMachineDetail(form.machine_id); }, [form.machine_id, loadMachineDetail]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.machine_id) { toast.error("Machine is required"); return; }
    if (!form.anchor_due_date) { toast.error("Anchor due date is required"); return; }
    if (form.frequency === "custom_days") {
      const n = parseInt(form.custom_days, 10);
      if (!n || n < 1 || n > 3650) { toast.error("Custom days must be 1–3650"); return; }
    }

    setSaving(true);
    try {
      const payload = {
        frequency: form.frequency,
        custom_days: form.frequency === "custom_days" ? parseInt(form.custom_days, 10) : null,
        anchor_due_date: form.anchor_due_date,
        active: form.active,
        notes: form.notes || null,
      };
      if (isEdit) {
        await api.patch(`/service-schedules/${id}`, payload);
        toast.success("Schedule updated");
        navigate("/scheduling/schedules");
      } else {
        payload.machine_id = form.machine_id;
        await api.post("/service-schedules", payload);
        toast.success("Schedule created");
        navigate("/scheduling/schedules");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="schedule-form-page">
      <PageHeader
        testId="schedule-form-header"
        title={isEdit ? "Edit service schedule" : "New service schedule"}
        subtitle="Anchors the recurring service cadence for one machine."
        actions={
          <Button asChild variant="outline" size="sm" data-testid="schedule-form-back">
            <Link to="/scheduling/schedules"><ArrowLeft className="h-4 w-4" /> Back</Link>
          </Button>
        }
      />

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-md">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Cpu className="h-4 w-4 text-primary" /> Machine
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEdit ? (
                <div className="text-sm">
                  {selectedMachine ? (
                    <>
                      <Link to={`/machines/${selectedMachine.id}`} className="font-medium hover:text-primary" data-testid="schedule-form-machine-link">
                        {selectedMachine.make} {selectedMachine.model}
                      </Link>
                      <div className="text-xs text-muted-foreground font-mono">Serial {selectedMachine.serial}</div>
                    </>
                  ) : <span className="text-muted-foreground">Loading machine…</span>}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="machine">Machine</Label>
                  <EntityCombobox
                    items={machines}
                    value={form.machine_id || ""}
                    onChange={(v) => update({ machine_id: v })}
                    placeholder="Select machine"
                    emptyMessage="No machines match."
                    getLabel={(m) => [m.make, m.model].filter(Boolean).join(" ") || "(unnamed)"}
                    getSecondary={(m) => `Serial ${m.serial || "—"}`}
                    testid="schedule-form-machine"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Repeat className="h-4 w-4 text-primary" /> Cadence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="freq">Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => update({ frequency: v })}>
                    <SelectTrigger id="freq" data-testid="schedule-form-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((f) => (<SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {form.frequency === "custom_days" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="days">Every N days</Label>
                    <Input
                      id="days"
                      type="number"
                      min={1}
                      max={3650}
                      value={form.custom_days}
                      onChange={(e) => update({ custom_days: e.target.value })}
                      placeholder="e.g. 45"
                      data-testid="schedule-form-custom-days"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="anchor">Anchor due date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="anchor"
                        type="button"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-9",
                          !form.anchor_due_date && "text-muted-foreground"
                        )}
                        data-testid="schedule-form-anchor"
                      >
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        {form.anchor_due_date
                          ? formatDateOnly(form.anchor_due_date)
                          : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.anchor_due_date ? new Date(`${form.anchor_due_date}T00:00:00`) : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          const y = d.getFullYear();
                          const m = String(d.getMonth() + 1).padStart(2, "0");
                          const day = String(d.getDate()).padStart(2, "0");
                          update({ anchor_due_date: `${y}-${m}-${day}` });
                        }}
                        initialFocus
                        data-testid="schedule-form-anchor-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    The originally-planned next service. All future dates step forward from this.
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => update({ notes: e.target.value })}
                  placeholder="Optional — anything the office needs to remember about this cadence."
                  data-testid="schedule-form-notes"
                />
              </div>
              <div className="flex items-center justify-between rounded-sm border border-border bg-card p-3">
                <div>
                  <div className="text-sm font-medium">Active</div>
                  <div className="text-xs text-muted-foreground">Paused schedules don&apos;t populate Due Soon.</div>
                </div>
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => update({ active: v })}
                  data-testid="schedule-form-active"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          {selectedMachine && (
            <MetaCard
              title="Machine context"
              fields={[
                { label: "Customer", value: selectedMachine.customer?.name || "—" },
                { label: "Site", value: selectedMachine.site?.name || "—" },
                { label: "Type", value: selectedMachine.machine_type?.name || "—" },
                { label: "Next due", value: selectedMachine.next_due_at || "—", testId: "schedule-form-next-due" },
              ]}
            />
          )}
          <MetaCard
            title="Metadata"
            fields={[
              { label: "Created", value: meta.created_at ? formatDate(meta.created_at) : "—" },
              { label: "Updated", value: meta.updated_at ? formatDate(meta.updated_at) : "—" },
            ]}
          />
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/scheduling/schedules")} data-testid="schedule-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="schedule-form-submit">
              <Save className="h-4 w-4" />
              {saving ? "Saving…" : isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
